/**
 * 03/09/2026 (pedido do dono) — modal dos avisos da importação de ponto /
 * sincronização, AGRUPADOS POR FUNCIONÁRIO (a listagem solta de textos amarelos
 * era ilegível). Uma linha por grupo com a qtde em Badge; o clique expande o
 * detalhe (Collapse — Mantine v9: prop `expanded`). No rodapé, "Avaliar com IA"
 * chama POST /competencias/:id/ia/avaliar-avisos com TODOS os avisos (flat) e
 * mostra o resumo devolvido (markdown simples) — a IA só resume, nunca age.
 */
import { useMemo, useState } from "react";
import { Badge, Button, Collapse, Group, Modal, Paper, Text } from "@mantine/core";
import { IconChevronDown, IconChevronRight, IconSparkles } from "@tabler/icons-react";
import { api } from "./api";
import { notificarErroApi } from "./componentes/notificacoes";
import { TextoMarkdownSimples } from "./ia-chat-drawer";
import { agruparAvisosPorFuncionario } from "./ponto-avisos";

export function PontoAvisosModal({
  aberto,
  onFechar,
  avisos,
  nomesFuncionarios,
  competenciaId,
}: {
  aberto: boolean;
  onFechar: () => void;
  avisos: string[];
  nomesFuncionarios: string[];
  competenciaId: number;
}) {
  const grupos = useMemo(() => agruparAvisosPorFuncionario(avisos, nomesFuncionarios), [avisos, nomesFuncionarios]);
  const [expandidos, setExpandidos] = useState<Record<string, boolean>>({});
  const [avaliando, setAvaliando] = useState(false);
  const [resumoIa, setResumoIa] = useState<string | null>(null);

  /** "Avaliar com IA" (03/09/2026): manda a lista FLAT dos avisos; o resumo em
   *  markdown simples aparece num Paper dentro do próprio modal. */
  async function avaliarComIa() {
    try {
      setAvaliando(true);
      const r = await api.post<{ resumo: string }>(`/competencias/${competenciaId}/ia/avaliar-avisos`, { avisos });
      setResumoIa(r.resumo);
    } catch (e) {
      notificarErroApi(e, "Não deu para avaliar os avisos com a IA");
    } finally {
      setAvaliando(false);
    }
  }

  return (
    <Modal
      opened={aberto}
      onClose={onFechar}
      title={`Avisos da importação/sincronização (${avisos.length})`}
      size="lg"
      centered
      data-testid="modal-avisos-ponto"
    >
      <Text size="xs" c="dimmed" mb="sm">
        agrupados por funcionário; a IA resume o que merece atenção
      </Text>
      {grupos.map((g) => {
        const expandido = !!expandidos[g.nome];
        return (
          <div key={g.nome} style={{ marginBottom: 4 }}>
            <Group
              gap="sm"
              py={4}
              style={{ cursor: "pointer" }}
              onClick={() => setExpandidos((atual) => ({ ...atual, [g.nome]: !expandido }))}
              data-testid={`grupo-aviso-${g.nome}`}
            >
              {expandido ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
              <Text size="sm" fw={600}>
                {g.nome}
              </Text>
              <Badge variant="light" color="yellow">
                {g.avisos.length}
              </Badge>
            </Group>
            <Collapse expanded={expandido}>
              {g.avisos.map((a, i) => (
                <Text key={i} size="xs" ml={26} mb={2} style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {a}
                </Text>
              ))}
            </Collapse>
          </div>
        );
      })}
      {resumoIa && (
        <Paper withBorder p="sm" mt="sm" data-testid="resumo-ia-avisos">
          <Group gap="xs" mb={4}>
            <IconSparkles size={14} color="var(--mantine-color-violet-7)" />
            <Text size="xs" fw={700}>
              Resumo da IA
            </Text>
          </Group>
          <TextoMarkdownSimples texto={resumoIa} />
        </Paper>
      )}
      <Group justify="flex-end" mt="md">
        <Button variant="light" leftSection={<IconSparkles size={14} />} onClick={() => void avaliarComIa()} loading={avaliando}>
          Avaliar com IA
        </Button>
      </Group>
    </Modal>
  );
}
